import type { SafeAny } from '@/common/types';
import type { StudyConfigParams } from '@/modules/study/types';
import {
  fetchEpochSizeData,
  getCachedEpochConfig,
  normalizeEpochSize,
  preloadAllEpochData,
  preloadAdjacentEpochData,
  storeCurrentEpochData
} from './studyHelpers';
import { useRoute } from 'vue-router';

export enum NotificationTypes {
  Success = 'success',
  Warning = 'warning',
  Error = 'danger'
}

const PRELOAD_DELAY = 1000; // delay in milliseconds

export const getStudyData = async (
  {
    rootState,
    commit
  }: {
    rootState: SafeAny;
    commit: (type: string, payload: SafeAny, options?: SafeAny) => void;
  },
  payload: StudyConfigParams
): Promise<void> => {
  const { id, epochSize = 30, notch = 'True', refreshOnly } = payload || {};
  const band = 'False';
  const normalizedEpochSize = normalizeEpochSize(epochSize);
  const route = useRoute();
  const studyID = (id || route.params.id).toString();
  const cachedConfig = getCachedEpochConfig(rootState, studyID, normalizedEpochSize, notch, band);

  rootState.study.config.isLoading = false;
  rootState.study.config.error = undefined;

  try {
    if (!refreshOnly && cachedConfig) {
      rootState.study.config.data = {
        ...cachedConfig,
        id: studyID,
        epochSize,
        notch,
        band
      };
      rootState.study.config.isLoading = false;

      if (!cachedConfig.sets?.[normalizedEpochSize]?.length) {
        rootState.epoch.epochData.data = undefined;
      }
      return;
    }

    const currentEpochData = await fetchEpochSizeData(
      rootState,
      studyID,
      normalizedEpochSize,
      notch,
      band,
      Boolean(refreshOnly)
    );

    storeCurrentEpochData(rootState, studyID, normalizedEpochSize, notch, band, currentEpochData);

    rootState.study.config.data = {
      ...rootState.study.config.data,
      id: studyID,
      epochSize,
      notch,
      band
    };

    rootState.study.config.isLoading = false;

    if (!currentEpochData.data.length) {
      rootState.epoch.epochData.data = undefined;
    }

    void preloadAllEpochData(rootState, studyID, notch, band);

    setTimeout(() => {
      preloadAdjacentEpochData(rootState, studyID, notch, band, normalizedEpochSize);
    }, PRELOAD_DELAY);
  } catch (error: SafeAny) {
    rootState.study.config.isLoading = false;
    rootState.study.config.data = {};

    commit(
      'addNotification',
      {
        i18nTitle: 'errorGetStudy',
        type: NotificationTypes.Error,
        i18nDescription: error.message
      },
      { root: true }
    );
  }
};
